/**
 * Ory Kratos account recovery ("forgot password") via emailed code.
 *
 * Submitting the right code makes Kratos issue a privileged session and answer
 * 422 with a settings-flow URL; the new password is then set on that flow.
 */

import { LoginFlow, csrfToken, messagesOf } from "./kratosLogin";
import { AlreadySignedIn } from "./kratosRegistration";

const KRATOS =
  process.env.REACT_APP_KRATOS_PUBLIC_URL || "http://localhost:4433";

export type RecoveryFlow = LoginFlow;

export type RecoveryResult =
  | { kind: "codeSent"; flow: RecoveryFlow; message: string }
  | { kind: "recovered"; settingsFlowId: string }
  | { kind: "expired" }
  | { kind: "error"; flow?: RecoveryFlow; message: string };

type Payload = (Partial<RecoveryFlow> & { error?: { id?: string; message?: string }; redirect_browser_to?: string }) | null;

export async function createRecoveryFlow(): Promise<RecoveryFlow> {
  const res = await fetch(`${KRATOS}/self-service/recovery/browser`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const payload = (await res.json().catch(() => null)) as Payload;
    if (payload?.error?.id === "session_already_available") throw new AlreadySignedIn();
    throw new Error(`Could not start a recovery flow (HTTP ${res.status})`);
  }
  return (await res.json()) as RecoveryFlow;
}

async function post(flow: RecoveryFlow, body: Record<string, unknown>): Promise<{ res: Response; payload: Payload }> {
  const res = await fetch(flow.ui.action, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ...body, csrf_token: csrfToken(flow) }),
  });
  const payload = (await res.json().catch(() => null)) as Payload;
  return { res, payload };
}

function expired(res: Response, payload: Payload): boolean {
  return res.status === 410 || payload?.error?.id === "self_service_flow_expired";
}

/** Asks Kratos to email a recovery code. Kratos reports this as 400 + a code input. */
export async function requestRecoveryCode(flow: RecoveryFlow, email: string): Promise<RecoveryResult> {
  const { res, payload } = await post(flow, { method: "code", email });
  if (expired(res, payload)) return { kind: "expired" };
  if (!payload?.ui) {
    return { kind: "error", message: payload?.error?.message || "Could not send a recovery code." };
  }
  const updated = payload as RecoveryFlow;
  const hasCodeInput = updated.ui.nodes.some((n) => n.attributes.name === "code");
  if (hasCodeInput) {
    return {
      kind: "codeSent",
      flow: updated,
      message:
        messagesOf(updated, "info")[0] ||
        "We emailed you a recovery code. Enter it below.",
    };
  }
  return {
    kind: "error",
    flow: updated,
    message: messagesOf(updated, "error")[0] || "Could not send a recovery code.",
  };
}

export async function submitRecoveryCode(flow: RecoveryFlow, code: string): Promise<RecoveryResult> {
  const { res, payload } = await post(flow, { method: "code", code });
  if (expired(res, payload)) return { kind: "expired" };

  if (res.status === 422 && payload?.error?.id === "browser_location_change_required") {
    const target = payload.redirect_browser_to || "";
    const settingsFlowId = new URL(target, KRATOS).searchParams.get("flow") || "";
    if (settingsFlowId) return { kind: "recovered", settingsFlowId };
    return { kind: "error", message: "Recovery succeeded but no password form was offered." };
  }

  if (!payload?.ui) {
    return { kind: "error", message: payload?.error?.message || "Recovery failed." };
  }
  const updated = payload as RecoveryFlow;
  return {
    kind: "error",
    flow: updated,
    message: messagesOf(updated, "error")[0] || "The recovery code is invalid or has already been used.",
  };
}
